import { useEffect } from "react";

/**
 * Tiny per-route metadata hook for Cierge Labs.
 *
 * Updates `document.title`, the meta description, and the og:/twitter: tags
 * when a route mounts. Resets to the document defaults from `public/index.html`
 * when the route unmounts.
 *
 * Intentionally minimal — no react-helmet dependency. The base shell in
 * `public/index.html` already carries the Cierge Labs default metadata that
 * crawlers see for the root. This hook layers route-specific overrides on
 * top of that base.
 */
const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const parts = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (parts) el.setAttribute(parts[1], parts[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const getMeta = (selector) =>
  document.head.querySelector(selector)?.getAttribute("content") || "";

export function usePageMeta({ title, description, ogTitle, ogDescription, ogUrl }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta('meta[name="description"]'),
      ogTitle: getMeta('meta[property="og:title"]'),
      ogDescription: getMeta('meta[property="og:description"]'),
      ogUrl: getMeta('meta[property="og:url"]'),
      twitterTitle: getMeta('meta[name="twitter:title"]'),
      twitterDescription: getMeta('meta[name="twitter:description"]'),
    };

    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);
    if (ogTitle) setMeta('meta[property="og:title"]', "content", ogTitle);
    if (ogDescription) setMeta('meta[property="og:description"]', "content", ogDescription);
    if (ogUrl) setMeta('meta[property="og:url"]', "content", ogUrl);
    if (ogTitle) setMeta('meta[name="twitter:title"]', "content", ogTitle);
    if (ogDescription) setMeta('meta[name="twitter:description"]', "content", ogDescription);

    return () => {
      document.title = prev.title;
      setMeta('meta[name="description"]', "content", prev.description);
      setMeta('meta[property="og:title"]', "content", prev.ogTitle);
      setMeta('meta[property="og:description"]', "content", prev.ogDescription);
      setMeta('meta[property="og:url"]', "content", prev.ogUrl);
      setMeta('meta[name="twitter:title"]', "content", prev.twitterTitle);
      setMeta('meta[name="twitter:description"]', "content", prev.twitterDescription);
    };
  }, [title, description, ogTitle, ogDescription, ogUrl]);
}
